import { useMemo, useState } from "react";
import { AlertTriangle, CheckSquare, ClipboardCheck, RefreshCw, Trash2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAdminLanguage } from "@/i18n/AdminLanguage";

const translations = {
  es: {
    repairTitle:"Limpiezas web pendientes",repairIntro:"D1 puede haber eliminado ya estas fiestas. Comprueba primero D1 antes de completar la limpieza web.",
    repairButton:"Reparar limpieza web",repairConfirm:"D1 debe haber eliminado ya estas fiestas. ¿Comprobar y limpiar sus fichas web pendientes?",repairPrompt:"Escribe REPARAR WEB para confirmar:",
    repairDone:"Limpieza web completada.",repairError:"No se pudo completar la limpieza web",partialError:"D1 eliminó las fiestas, pero falta limpiar las fichas web. NO repitas el borrado. Usa Reparar limpieza web.",
    qrUsed:"QR usados",receipts:"Comprobantes",totalOrders:"Total de pedidos",approvedRevenue:"Ingresos aprobados",promoterSales:"Ventas de promotores",promoterNet:"Por KNDLA (promotores)",transferRequests:"Solicitudes de transferencia",manualTickets:"Entradas manuales",
    linkedWarning:"También se eliminará su ficha vinculada de la página web.",activeWarning:"ATENCIÓN: esta fiesta está activa. Sus ventas y QR dejarán de funcionar al eliminarla.",
    title: "Eliminar fiestas definitivamente",
    summary: "El administrador puede eliminar cualquier fiesta, incluso activa o con ventas reales. Se borrarán sus pedidos, QR, movimientos de promotores y vínculos a la web después de la confirmación explícita.",
    note: "IRREVERSIBLE: guarda primero un respaldo de D1 y de la web. Los QR enviados dejarán de funcionar; los mensajes ya enviados por Telegram no pueden retirarse automáticamente. Se conservan la auditoría y los perfiles generales.",
    search: "Buscar por nombre o fecha", select: "Seleccionar", selected: "seleccionadas", noEvents: "No hay fiestas para elegir.",
    limit: "Máximo 10 fiestas por operación.", review: "Revisar datos antes de eliminar", checking: "Consultando D1…",
    allowed: "Lista para eliminar", blocked: "Requiere revisión técnica", orders: "Pedidos", tickets: "QR", noData: "Sin pedidos ni QR",
    caution: "Revisa los importes, QR y transferencias. Se eliminarán aunque sean operaciones reales. No hay modo de prueba.",
    phrase: "Escribe exactamente", execute: "Eliminar definitivamente", busy: "Eliminando…",
    done: "Fiestas eliminadas de D1 y fichas vinculadas de la web. Actualizando el informe…",
    invalid: "Algunas fiestas tienen relaciones inconsistentes. No se pueden borrar junto con otras hasta corregirlas.",
    oneLast: "Esta operación es IRREVERSIBLE. ¿Confirmas que revisaste la vista previa y el respaldo de D1?",
    empty: "Elige una o varias fiestas para comenzar.",
    refresh: "Actualizar vista previa", clear: "Limpiar selección", hint: "La selección no elimina datos hasta completar la confirmación.",
    prevTitle: "Vista previa de los datos que desaparecerán", safeOnly:"Verifica los datos de cada fiesta. Solo se bloquearán relaciones inconsistentes.",
  },
  en: {
    repairTitle:"Pending website cleanup",repairIntro:"D1 may have deleted these parties already. Verify D1 before cleaning up the website records.",
    repairButton:"Repair website cleanup",repairConfirm:"D1 should have deleted these parties. Verify D1 and clean up pending website records?",repairPrompt:"Type REPARAR WEB to confirm:",
    repairDone:"Website cleanup completed.",repairError:"Website cleanup could not be completed",partialError:"D1 deleted the parties but website cleanup remains pending. DO NOT delete them again. Use Repair website cleanup.",
    qrUsed:"Used QR",receipts:"Receipts",totalOrders:"Order totals",approvedRevenue:"Approved revenue",promoterSales:"Promoter sales",promoterNet:"KNDLA share (promoters)",transferRequests:"Transfer requests",manualTickets:"Manual tickets",
    linkedWarning:"Its linked website listing will also be deleted.",activeWarning:"WARNING: this party is active. Sales and QR codes will stop working when deleted.",
    title: "Permanently delete parties",
    summary: "Admins may delete any party, including active parties and real sales. Orders, QR codes, promoter transactions and website links will be deleted after explicit confirmation.",
    note: "IRREVERSIBLE: back up D1 and the website first. Issued QR codes will stop working, but existing Telegram messages cannot be recalled. Audit and shared profiles remain.",
    search:"Search by name or date",select:"Select",selected:"selected",noEvents:"No parties to choose.",
    limit:"Maximum 10 parties per operation.",review:"Review before deleting",checking:"Checking D1…",
    allowed:"Ready to delete",blocked:"Needs technical review",orders:"Orders",tickets:"QR",noData:"No orders or QR",
    caution:"Review revenue, tickets and settlements. Real transactions will be deleted. There is no test mode.",
    phrase:"Type exactly",execute:"Delete permanently",busy:"Deleting…",done:"Parties deleted from D1 and linked website records. Refreshing…",
    invalid:"Some parties have inconsistent references. Correct these before deleting.",
    oneLast:"This action CANNOT BE UNDONE. Have you reviewed the preview and saved the D1 backup?",
    empty:"Select one or more parties to start.",refresh:"Refresh preview",clear:"Clear selection",hint:"Selection does not delete anything until you confirm.",
    prevTitle:"Preview of data to be deleted",safeOnly:"Review each party. Only inconsistent relationships are blocked.",
  },
  ru: {
    repairTitle:"Ожидает очистки сайта",repairIntro:"Возможно, эти вечеринки уже удалены из D1. Перед очисткой сайта система перепроверит D1.",
    repairButton:"Завершить очистку сайта",repairConfirm:"Вечеринки должны быть удалены из D1. Проверить D1 и удалить оставшиеся записи сайта?",repairPrompt:"Для подтверждения введите REPARAR WEB:",
    repairDone:"Очистка сайта завершена.",repairError:"Не удалось завершить очистку сайта",partialError:"Вечеринки удалены из D1, но на сайте остались записи. НЕ повторяйте удаление. Используйте функцию очистки сайта.",
    qrUsed:"Использовано QR",receipts:"Квитанции",totalOrders:"Всего заказов",approvedRevenue:"Подтверждённая выручка",promoterSales:"Продажи промоутеров",promoterNet:"Доля KNDLA (промоутеры)",transferRequests:"Запросы переводов",manualTickets:"Ручные билеты",
    linkedWarning:"Связанная запись на сайте также будет удалена.",activeWarning:"ВНИМАНИЕ: вечеринка активна. После удаления продажи и QR перестанут работать.",
    title:"Безвозвратно удалить вечеринки",
    summary:"Администратор может безвозвратно удалить любую вечеринку, даже активную или с реальными продажами. После подтверждения будут удалены заказы, QR, продажи промоутеров и связи с сайтом.",
    note:"НЕОБРАТИМО: сначала сохраните резервную копию D1 и сайта. QR перестанут работать, но отправленные сообщения Telegram останутся. Журнал аудита и общие профили сохраняются.",
    search:"Поиск по названию или дате",select:"Выбрать",selected:"выбрано",noEvents:"Нет вечеринок для выбора.",
    limit:"Не более 10 вечеринок за операцию.",review:"Проверить перед удалением",checking:"Проверяем D1…",
    allowed:"Готова к удалению",blocked:"Нужна техническая проверка",orders:"Заказы",tickets:"QR",noData:"Нет заказов и QR",
    caution:"Проверьте суммы, QR и переводы. Реальные операции также будут удалены. Тестового режима нет.",
    phrase:"Введите точно",execute:"Удалить навсегда",busy:"Удаление…",done:"Вечеринки удалены из D1 и связанного каталога. Обновляем отчёт…",
    invalid:"Некоторые вечеринки имеют несогласованные связи между записями. Исправьте их до удаления.",
    oneLast:"ОПЕРАЦИЯ НЕОБРАТИМА. Вы проверили список и резервную копию D1?",
    empty:"Выберите одну или несколько вечеринок.",refresh:"Обновить проверку",clear:"Очистить выбор",hint:"Выбор не удаляет данные без подтверждения.",
    prevTitle:"Предварительный просмотр удаляемых данных",safeOnly:"Проверьте данные каждой вечеринки. Блокируются только несогласованные связи.",
  }
};
const boxClass = "rounded-2xl border border-red-400/20 bg-ash/85 p-5";
const inputClass = "w-full rounded-lg border border-white/15 bg-coal px-3 py-2 text-sm text-white outline-none focus:border-red-300";

export default function AdminKndlaPurge({ events = [], onDeleted }) {
  const { lang } = useAdminLanguage();
  const t = translations[lang] || translations.es;
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState([]);

  const choices = useMemo(() => events.filter(e =>
    `${e.name || ""} ${e.event_date || ""}`.toLowerCase().includes(search.trim().toLowerCase())
  ).sort((a, b) => Number(b.id) - Number(a.id)), [events, search]);

  const allowed = Boolean(preview?.events?.length) && preview.events.every(e => e.eligible);
  const required = `ELIMINAR ${selected.length}`;

  function updateSelection(id, active) {
    const value = String(id);
    setSelected(prev => active ? (prev.length >= 10 ? prev : [...prev, value]) : prev.filter(x => x !== value));
    setPreview(null); setConfirmPhrase(""); setError(""); setNotice("");
  }

  async function loadPending() {
    try {
      const {data} = await api.get("/kndla/purge/repairs", { timeout: 18000 });
      setPending(Array.isArray(data?.pending) ? data.pending : []);
    } catch { setPending([]); }
  }

  async function repair(record) {
    if (busy) return;
    if (!window.confirm(t.repairConfirm)) return;
    const typed=window.prompt(t.repairPrompt);
    if (typed!=="REPARAR WEB") return;
    setBusy(true);setError("");setNotice("");
    try {
      await api.post(`/kndla/purge/repair/${record.id}`, {confirmation:"REPARAR WEB"}, {timeout:45000});
      setNotice(t.repairDone);
      await loadPending();
      await onDeleted?.(record.event_ids || []);
    } catch(err){setError(fmtErr(err,t.repairError));}
    finally{setBusy(false);}
  }

  async function review() {
    if (!selected.length || busy) return;
    setBusy(true); setError(""); setNotice(""); setPreview(null); setConfirmPhrase("");
    try {
      const { data } = await api.get("/kndla/purge/preview", {
        params: { ids: selected.join(",") }, timeout: 30000,
      });
      if (!Array.isArray(data?.events) || typeof data?.token !== "string") throw Error("Invalid preview");
      setPreview(data);
    } catch (err) { setError(fmtErr(err, "No se pudo comprobar D1")); }
    finally { setBusy(false); }
  }

  async function execute() {
    if (!allowed || !preview || confirmPhrase !== required || busy) return;
    if (!window.confirm(t.oneLast)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await api.post("/kndla/purge/execute", {
        event_ids: selected.map(Number), preview_token: preview.token, confirmation: confirmPhrase,
      }, { timeout: 45000 });
      if (data?.partial) {
        setError(t.partialError);
        setSelected([]);setPreview(null);setConfirmPhrase("");
        await loadPending();
        await onDeleted?.(selected.map(Number));
        return;
      }
      if (!data?.ok || Number(data?.deleted_count) !== selected.length) throw Error("Respuesta de eliminación inconsistente");
      const removed = [...selected];
      setSelected([]); setPreview(null); setConfirmPhrase("");
      setNotice(t.done);
      await onDeleted?.(removed);
      await loadPending();
    } catch (err) {
      setError(fmtErr(err, "No se pudo eliminar. Verifica los datos antes de reintentar"));
      setPreview(null); setConfirmPhrase("");
      await loadPending();
    } finally { setBusy(false); }
  }

  return <section className={boxClass} aria-label={t.title}>
    <button type="button" onClick={() => {setExpanded(x => !x); if(!expanded) loadPending();}}
      className="flex w-full items-center justify-between gap-2 text-left text-red-200">
      <span className="flex items-center gap-2 font-syne text-lg font-bold"><Trash2 size={18}/>{t.title}</span>
      <span className="text-xs font-bold">{expanded ? "−" : "+"}</span>
    </button>
    {!expanded ? <p className="mt-2 text-xs text-white/45">{t.summary}</p> : <>
      <p className="mt-3 text-xs leading-5 text-white/60">{t.summary}</p>
      <p className="mt-2 flex items-start gap-2 text-xs leading-5 text-amber-200"><AlertTriangle size={16} className="mt-0.5 shrink-0"/>{t.note}</p>
      <p className="mt-1 text-xs text-white/45">{t.limit}</p>
      <label className="mt-4 block"><span className="mb-2 block text-[11px] text-white/50">{t.search}</span>
        <input value={search} onChange={e => setSearch(e.target.value)} className={inputClass} placeholder={t.search}/>
      </label>
      <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-white/10 divide-y divide-white/10">
        {choices.map(e => {
          const checked = selected.includes(String(e.id));
          return <label key={e.id} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-xs text-white/80">
            <input type="checkbox" checked={checked} disabled={busy || (!checked && selected.length >= 10)}
              onChange={x => updateSelection(e.id, x.target.checked)} className="h-4 w-4 accent-red-400" />
            <span className="min-w-0 flex-1 break-words font-semibold">{e.name}<span className="ml-2 font-normal text-white/40">#{e.id} · {e.event_date?.slice(0, 16) || "—"} · {e.status}</span></span>
          </label>;
        })}
        {!choices.length && <p className="p-4 text-xs text-white/40">{t.noEvents}</p>}
      </div>
      <p className="mt-2 text-xs text-white/50">{selected.length} {t.selected} · {t.hint}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={review} disabled={!selected.length || busy}
          className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-4 py-2 text-xs font-bold text-white disabled:opacity-40">
          {busy ? <RefreshCw size={14} className="animate-spin"/> : <ClipboardCheck size={14}/>}{busy ? t.checking : (preview ? t.refresh : t.review)}
        </button>
        <button type="button" onClick={() => {setSelected([]);setPreview(null);setConfirmPhrase("");setError("");}}
          disabled={busy} className="rounded-lg border border-white/15 px-4 py-2 text-xs text-white/70 disabled:opacity-40">{t.clear}</button>
      </div>
      {error && <p role="alert" className="mt-3 text-xs leading-5 text-red-300">{error}</p>}
      {notice && <p role="status" className="mt-3 text-xs leading-5 text-emerald-300">{notice}</p>}
      {pending.length > 0 && <div className="mt-4 rounded-xl border border-amber-400/40 p-3 text-xs text-amber-100">
        <p className="font-bold">{t.repairTitle}</p>
        <p className="mt-1">{t.repairIntro}</p>
        {pending.map(item=><div key={item.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-2">
          <span>Fiestas #{(item.event_ids||[]).join(", #")} · {item.state}</span>
          <button type="button" disabled={busy} onClick={()=>repair(item)}
            className="rounded-lg border border-amber-300/40 px-3 py-1.5 font-bold disabled:opacity-50">{t.repairButton}</button>
        </div>)}
      </div>}
      {preview && <div className="mt-4 rounded-xl border border-white/10 bg-coal/70 p-3">
        <div className="flex items-center gap-2 text-sm font-bold text-white"><CheckSquare size={16}/>{t.prevTitle}</div>
        <p className="mt-1 text-xs text-white/50">{t.safeOnly}</p>
        <div className="mt-3 space-y-2">
          {preview.events.map(e => <div key={e.id} className="rounded-lg border border-white/10 p-3 text-xs">
            <p className="font-semibold text-white">#{e.id} · {e.name || "—"}</p>
            <p className={`mt-1 font-bold ${e.eligible ? "text-emerald-300" : "text-red-300"}`}>{e.eligible ? t.allowed : t.blocked}</p>
            <div className="mt-2 grid grid-cols-1 gap-1 text-white/70 sm:grid-cols-2">
              <p>{t.orders}: <strong>{Number(e.orders_count||0)}</strong> · {t.tickets}: <strong>{Number(e.tickets_count||0)}</strong></p>
              <p>{t.qrUsed}: <strong>{Number(e.tickets_used||0)}</strong> · {t.receipts}: <strong>{Number(e.receipts||0)}</strong></p>
              <p>{t.totalOrders}: <strong>{Number(e.total_ordered_rub||0).toLocaleString("ru-RU")} ₽</strong></p>
              <p>{t.approvedRevenue}: <strong>{Number(e.approved_rub||0).toLocaleString("ru-RU")} ₽</strong></p>
              <p>{t.promoterSales}: <strong>{Number(e.promoter_sales||0)}</strong></p>
              <p>{t.promoterNet}: <strong>{Number(e.promoter_net_rub||0).toLocaleString("ru-RU")} ₽</strong></p>
              <p>{t.transferRequests}: <strong>{Number(e.payment_requests||0)}</strong></p>
              <p>{t.manualTickets}: <strong>{Number(e.manual_issues||0)}</strong></p>
              {e.website_ref && <p className="sm:col-span-2 text-amber-200">{t.linkedWarning}</p>}
              {e.status === "active" && <p className="sm:col-span-2 text-red-200">{t.activeWarning}</p>}
            </div>
            {!e.eligible && e.reasons?.map((reason,i) => <p key={i} className="mt-1 text-red-200/85">• {reason}</p>)}
          </div>)}
        </div>
        <p className="mt-3 text-xs leading-5 text-amber-200">{t.caution}</p>
        {allowed ? <div className="mt-4 space-y-3">
          <label className="block text-xs text-white/70">{t.phrase}: <strong className="select-all font-mono text-white">{required}</strong>
            <input value={confirmPhrase} onChange={e => setConfirmPhrase(e.target.value)} className={`${inputClass} mt-2`} autoComplete="off"/>
          </label>
          <button type="button" onClick={execute} disabled={busy || confirmPhrase !== required}
            className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-40">
            <Trash2 size={15}/>{busy ? t.busy : t.execute}
          </button>
        </div> : <p className="mt-3 text-xs text-red-300">{t.invalid}</p>}
      </div>}
    </>}
  </section>;
}
